<template>
  <div class="modal fade show adjustment-detail-modal" aria-labelledby="adjustmentDetail">
    <div class="modal-dialog modal-dialog-centered modal-custom">
      <div class="modal-content">
        <div class="row">
          <div class="col-12">
            <div class="d-flex justify-content-between align-items-center px-1 my-1">
              <h1 class="mb-0">Stock Adjustment Detail</h1>
              <button @click="closeModal" type="button" class="btn-close" aria-label="Close"></button>
            </div>
          </div>
        </div>

        <hr class="mt-0">

        <div class="modal-body pb-5">
          <div v-if="loading" class="text-center py-5">Loading...</div>

          <div v-if="! loading && adjustment">
            <div class="row gy-1">
              <div class="col-12 col-md-3" v-if="adjustment.adjustment_no">
                <span class="highlight-row">Adjustment No: </span>
                <span>{{ adjustment.adjustment_no }}</span>
              </div>
              <div class="col-12 col-md-3" v-if="adjustment.date">
                <span class="highlight-row">Adjustment Date: </span>
                <span>{{ adjustment.date }}</span>
              </div>
              <div class="col-12 col-md-3" v-if="adjustment.warehouse_name">
                <span class="highlight-row">Warehouse: </span>
                <span>{{ adjustment.warehouse_name }}</span>
              </div>
              <div class="col-12 col-md-3" v-if="adjustment.account_head_name">
                <span class="highlight-row">Adjustment Account: </span>
                <span>{{ adjustment.account_head_name }}</span>
              </div>
              <div class="col-12 col-md-3" v-if="adjustment.project_name">
                <span class="highlight-row">Project: </span>
                <span>{{ adjustment.project_name }}</span>
              </div>
              <div class="col-12 col-md-3" v-if="adjustment.cost_centre_name">
                <span class="highlight-row">Cost Center: </span>
                <span>{{ adjustment.cost_centre_name }}</span>
              </div>
              <div class="col-12" v-if="adjustment.description">
                <span class="highlight-row">Memo: </span>
                <span>{{ adjustment.description }}</span>
              </div>
            </div>

            <div class="col-12 mt-8">
              <div class="table-responsive">
                <table class="table custom-table-padding">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th class="text-right">Quantity In</th>
                      <th class="text-right">Quantity Out</th>
                      <th class="text-right">Adjustment Qty</th>
                      <th>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(stock, i) in stockList" :key="i">
                      <td>{{ stock.product_name }}</td>
                      <td class="text-right">{{ stock.quantity_in }}</td>
                      <td class="text-right">{{ stock.quantity_out }}</td>
                      <td class="text-right">{{ stock.adjustment_qty }}</td>
                      <td>{{ stock.note }}</td>
                    </tr>
                  </tbody>
                </table>
                <p v-if="! stockList.length" class="text-center">No items</p>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-12 text-center pb-3">
            <button class="btn btn-secondary me-1" @click="closeModal">Close</button>
            <button
              class="btn btn-success"
              :disabled="approving || loading || ! adjustment"
              @click="approve"
            >
              Approve
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, inject } from 'vue'
import { useRoute } from 'vue-router'
import handleStock from '@/services/modules/stock'

const props = defineProps({
  adjustmentId: {
    type: [Number, String],
    required: true
  },
  approving: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['onApprove', 'onClose'])

const route = useRoute()
const showError = inject('showError')
const { fetchStockAdjustment } = handleStock()

const loading = ref(false)
const adjustment = ref(null)

const stockList = computed(() => {
  if (! adjustment.value) return []
  if (! adjustment.value.stocks) return []
  return adjustment.value.stocks
})

const closeModal = () => {
  emit('onClose')
}

const approve = () => {
  if (props.approving) return
  emit('onApprove', props.adjustmentId)
}

const getAdjustment = () => {
  loading.value = true
  const query = `?company_id=${route.params.companyId}`
  fetchStockAdjustment(query, props.adjustmentId)
    .then(res => {
      if (! res.status) return
      adjustment.value = res.data
    })
    .catch(() => showError('Failed to load stock adjustment detail'))
    .finally(() => (loading.value = false))
}

onMounted(() => {
  getAdjustment()
})
</script>

<style scoped>
.adjustment-detail-modal {
  display: block;
  background: #1c1d1c33;
}
.modal-custom {
  max-width: 75vw;
}
.highlight-row {
  font-weight: bold;
}
.mt-8 {
  margin-top: 8px;
}
</style>
